import { useDeleteDocument } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { PageHeader } from "@shared/components/PageHeader";
import { StepHelpers } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { downloadFileByUrl } from "@shared/utils/utils";
import { Download, FileText, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetSystemDataById, useUpdateDocuments } from "../../hooks";
import { SystemFormData } from "../../types";

interface DocsContactsStepProps {
  globalFormData: SystemFormData;
  updateGlobalFormData: (data: Partial<SystemFormData>) => void;
  editMode: boolean;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  allStepData?: Record<number, any>;
  onComplete?: (
    allStepData: any,
    completedSteps: number[],
    skippedSteps: number[]
  ) => void;
}

export const DocsContactsStep: React.FC<DocsContactsStepProps> = ({
  globalFormData,
  updateGlobalFormData,
  editMode,
  stepHelpers,
  currentStepIndex = 3,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
  allStepData,
  onComplete,
}) => {
  const { systemsId, action } = useParams();
  const { data: systemData } = useGetSystemDataById(Number(systemsId));
  const { toast } = useToast();
  const navigate = useNavigate();
  const dataLoadedRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API mutation hook
  const { mutateAsync: updateDocuments, isPending: isUpdating } =
    useUpdateDocuments("");

  const { mutateAsync: removeDocument, isPending: isDeleting } =
    useDeleteDocument("system");

  useEffect(() => {
    if (action === "edit" && systemData && !dataLoadedRef.current) {
      const documentsData = {
        documents: systemData?.uploadedDocuments || [],
      };

      updateGlobalFormData(documentsData);
      onDataChange?.(documentsData);

      dataLoadedRef.current = true;
    }
  }, [action, systemData, updateGlobalFormData, onDataChange]);

  const saveStepData = useCallback(
    (stepData: any) => {
      if (!stepHelpers) return;
      try {
        const dataToSave = {
          ...stepData,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };
        stepHelpers.setStepData(currentStepIndex, dataToSave);
      } catch (error) {}
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return {};
    try {
      return stepHelpers.getStepData(currentStepIndex) || {};
    } catch (error) {
      return {};
    }
  }, [stepHelpers, currentStepIndex]);

  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return {};
    try {
      return stepHelpers.getStepData(0) || {}; // Step 1 is at index 0
    } catch (error) {
      return {};
    }
  }, [stepHelpers]);

  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current && action !== "edit") {
      try {
        const savedStepData = getStepData();

        if (savedStepData && Object.keys(savedStepData).length > 0) {
          // Update global form data
          updateGlobalFormData({
            documents: savedStepData.documents || [],
          });

          // Call onDataChange to sync with stepper
          onDataChange?.(savedStepData);
        }
        dataLoadedRef.current = true;
      } catch (error) {
        dataLoadedRef.current = true;
      }
    }
  }, [
    stepHelpers,
    currentStepIndex,
    getStepData,
    onDataChange,
    updateGlobalFormData,
    action,
  ]);

  const removeFile = (index: number) => {
    removeDocument("10");
  };

  const handleComplete = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    setIsSubmitting(true);
    try {
      const formData = form.getValues();

      const apiFormData = new FormData();

      // Append documents if they exist
      if (Array.isArray(formData.documents) && formData.documents.length > 0) {
        formData.documents.forEach((doc) => {
          apiFormData.append("doc", doc);
        });
      }

      const response = await updateDocuments({
        systemId: Number(systemsId),
        formData: apiFormData,
      });

      // Get all step data for completion callback
      if (response) {
        if (onComplete && allStepData) {
          const completedSteps = Object.keys(allStepData).map(Number);
          onComplete(allStepData, completedSteps, []);
        }

        navigate("/ax/asset-registry/systems");
      }
    } catch (error) {
      if (setValidationError) {
        setValidationError(
          "An error occurred while finalizing the asset. Please try again."
        );
      }
      toast({
        title: "Error",
        description: "Failed to update asset. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    // Save current state before going back
    const currentData = {
      ...data,
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    };
    onDataChange?.(currentData);
    saveStepData(currentData);
    onPrevious?.();
  };

  const handleNext = () => {
    // Save current state before going forward
    const currentData = {
      ...data,
      navigatedForward: true,
      navigatedForwardAt: new Date().toISOString(),
    };
    onDataChange?.(currentData);
    saveStepData(currentData);

    if (isLastStep) {
      handleComplete();
    } else {
      onNext?.();
    }
  };

  const step1Data = getStep1Data();

  const formFields: FormField[] = [
    {
      name: "documents",
      type: "multi-file",
      className: "col-span-2",
      placeholder: "Upload system documents",
      required: false,
      multiple: true,
      accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
      helperText:
        "Examples: Site Plans, Environmental Permits, Geotechnical Reports, Security Plans",
      classes: FIELD_CLASSES,
      maxFiles: 10,
      maxSize: 1000,
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
  });

  // Handle field changes from DynamicForm
  const handleFieldChange = (fieldName: string, value: any) => {
    // Map form field names to globalFormData field names
    const globalUpdates: any = {};
    if (fieldName === "documents") {
      globalUpdates.documents = value;
    }

    updateGlobalFormData(globalUpdates);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="mb-6">
        <PageHeader
          title="Documentation & Contacts"
          subtitle="Upload supporting documentation and provide contact information for this system."
        />
        {/* Show asset info from step 1 */}
        {step1Data.assetId && (
          <p className="text-sm text-blue-600 mt-1">
            Asset ID: {step1Data.assetId} | Type: {step1Data.assetType}
          </p>
        )}
      </div>

      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-medium mb-4">
            Documentation & Contact Information
          </h3>
          <DynamicForm
            fields={formFields}
            form={form}
            onFieldChange={handleFieldChange}
            config={{
              layout: "vertical",
              showSubmitButton: false,
              showResetButton: false,
              spacing: "medium",
            }}
          />
        </div>

        {/* Display uploaded documents if they exist */}
        {systemData?.documents?.length > 0 && (
          <div>
            <p className="page-title mb-2">Uploaded Documents</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Show existing uploaded documents from asset detail */}
              {systemData?.documents?.map((document) => (
                <div
                  key={document.id}
                  className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {/* File Icon */}
                    <div className="flex-shrink-0">
                      <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {document.doc.split("/").pop() || "Document"}{" "}
                        {/* Extract filename from URL */}
                      </p>
                      <p className="text-xs text-gray-500">
                        Created: {document.createdDate}
                      </p>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      onClick={() => downloadFileByUrl(document.doc)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => removeFile(document.id)}
                      variant="ghost"
                      isLoading={isDeleting}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button
          onClick={handlePrevious}
          disabled={isFirstStep || isSubmitting}
          variant="outline"
        >
          Previous
        </Button>
        <Button onClick={handleNext} disabled={isValidating || isSubmitting}>
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
              {"Finalizing..."}
            </>
          ) : isValidating ? (
            "Validating..."
          ) : isLastStep ? (
            editMode ? (
              "Submit"
            ) : (
              "Submit"
            )
          ) : (
            "Next"
          )}
        </Button>
      </div>
    </div>
  );
};
